import { useEffect, useMemo, useState } from "react";
import {
  ProductsDataType,
  SpecOption,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import {
  Input,
  message,
  Modal,
  Popconfirm,
  Switch,
  Table,
  TableProps,
} from "antd";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { ProductsService } from "../../services/products.service";
import SelectCountry from "../../components/admin-pages/products/select-country/SelectCountry";
import SelectBrand from "../../components/admin-pages/products/select-brands/SelectBrands";
import SelectCategory from "../../components/admin-pages/products/select-category/SelectCategory";
import SelectOptions from "../../components/admin-pages/products/select-options/SelectOptions";
import { productsSchema } from "./schema/products";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import ProductImages from "../../components/admin-pages/products/product-images/ProductImages";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import DragImages from "../../components/admin-pages/products/drag-images/DragImages";
import SelectColor from "../../components/admin-pages/products/select-color/SelectColor";
import { FaPlus } from "react-icons/fa";
// import SelectWarehouse from "../../components/admin-pages/products/select-warehouse/SelectWarehouse";

const Products = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<ProductsDataType | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [products, setProducts] = useState<ProductsDataType[]>([]);
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [totalCount, setTotalCount] = useState<number>(0);

  // ✅ AZ hardcode yoxdur
  const [langCode, setLangCode] = useState<string>("");

  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [imageList, setImageList] = useState<any[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [selectedName, setSelectedName] = useState<string | undefined>(
    undefined,
  );
  const [selectedBrandName, setSelectedBrandName] = useState<
    string | undefined
  >(undefined);
  const [selectedColorName, setSelectedColorName] = useState<
    string | undefined
  >(undefined);
  const [selectedWarehouseName, setSelectedWarehouseName] = useState<
    string | undefined
  >(undefined);
  const [imagesModal, setImagesModal] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [key, setKey] = useState(0);
  const [searchValue, setSearchValue] = useState("");

  // default dil: langs[0].code
  const defaultLangCode = useMemo(() => langs?.[0]?.code || "", [langs]);

  useEffect(() => {
    if (!langCode && defaultLangCode) {
      setLangCode(defaultLangCode);
    }
  }, [defaultLangCode, langCode]);

  useEffect(() => {
    const fetchData = async () => {
      if (searchValue.trim() !== "") {
        try {
          const response = await ProductsService.getSearchData(searchValue);
          const list: ProductsDataType[] = response?.data?.products?.data ?? [];
          const searchedData = list.map(
            (item: ProductsDataType, index: number) => ({
              ...item,
              key: (currentPage - 1) * pageSize + index + 1,
            }),
          );
          setProducts(searchedData);
          setTotalCount(response?.data?.products?.total ?? searchedData.length);
        } catch (error) {
          console.error("Axtarış zamanı xəta baş verdi", error);
        }
      } else {
        setLoading(true);
        try {
          const response = await ProductsService.getAllProducts(
            currentPage,
            pageSize,
          );
          const list: ProductsDataType[] = response?.data?.data ?? [];
          const dataWithKeys = list.map(
            (item: ProductsDataType, index: number) => ({
              ...item,
              key: (currentPage - 1) * pageSize + index + 1,
            }),
          );
          setProducts(dataWithKeys);
          setTotalCount(response?.data?.count ?? 0);
        } catch (error) {
          console.error("Məhsulları gətirərkən xəta baş verdi", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [currentPage, pageSize, searchValue]);

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data ?? []),
    );
  }, []);

  const fetchProducts = async (page: number) => {
    const updatedData = await ProductsService.getAllProducts(page, pageSize);
    const list: ProductsDataType[] = updatedData?.data?.data ?? [];
    setProducts(
      list.map((item: ProductsDataType, index: number) => ({
        ...item,
        key: (page - 1) * pageSize + index + 1,
      })),
    );
    setTotalCount(updatedData?.data?.count ?? 0);
  };

  const handleAfterSubmit = async (page: number) => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    await fetchProducts(page);
    setLoading(false);
  };

  const {
    handleSubmit,
    values,
    handleChange,
    setFieldValue,
    resetForm,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      id: "",
      name: "",
      description: "",
      price: null as number | null,
      discount_price: null as number | null,
      weight: "",
      dimension: "",
      product_group: "STATIC_GROUP_NAME",
      model_code: "STATIC_MODEL_CODE_001",
      category_id: null as number | null,
      is_visible: true,
      country_id: null as number | null,
      brand_id: null as number | null,
      most_sels: false,
      is_new: true,
      translations: [] as {
        lang_code: string;
        name: string;
        description: string;
      }[],
      color_id: null as any,
      warehouse_id: null as any,
      specs: [] as SpecOption[],
      cover_image: "",
      images: [] as {
        path: string;
        rank: number;
      }[],
    },
    validationSchema: productsSchema,
    onSubmit: async (values) => {
      console.log(values);
      const finalValues = { ...values };

      if (isEditing && editingRecord) {
        if (editingRecord.cover_image === values.cover_image) {
          const payload = {
            name: values.name || "Test",
            description: values.description,
            is_visible: values.is_visible,
            price: values.price,
            discount_price: values.discount_price,
            weight: values.weight,
            dimension: values.dimension,
            category_id: values.category_id,
            country_id: values.country_id,
            brand_id: values.brand_id,
            color_id: values.color_id,
            specs: values.specs,
            most_sels: values.most_sels,
            is_new: values.is_new,
            warehouse_id: values.warehouse_id,
            translations: values.translations,
            images: values.images,
            product_group: "STATIC_GROUP_NAME",
            model_code: "STATIC_MODEL_CODE_001",
          };
          const response = await ProductsService.updateProduct({
            ...payload,
            id: editingRecord.id,
          });
          if (response.status === 200) {
            message.success("Məhsul uğurla yeniləndi!");
            await handleAfterSubmit(currentPage);
          } else {
            message.error(
              response.errorMessage || "Məhsulu yeniləmək alınmadı.",
            );
          }
        } else {
          const response = await ProductsService.updateProduct({
            ...finalValues,
            id: editingRecord.id,
          });
          if (response.status === 200) {
            message.success("Məhsul uğurla yeniləndi!");
            await handleAfterSubmit(currentPage);
          } else {
            message.error(
              response.errorMessage || "Məhsulu yeniləmək alınmadı.",
            );
          }
        }
      } else {
        const payload = {
          ...finalValues,
          model_code: "STATIC_MODEL_CODE_001", // İstədiyiniz statik dəyər
          product_group: "STATIC_GROUP_NAME", // İstədiyiniz statik dəyər
        };

        const response = await ProductsService.addProduct(payload);
        if (response.status === 201) {
          message.success("Məhsul uğurla əlavə edildi!");
          const updatedTotalCount = totalCount + 1;
          setTotalCount(updatedTotalCount);
          setCurrentPage(1);
          await handleAfterSubmit(1);
        } else {
          message.error(
            response.errorMessage || "Məhsul əlavə etmək alınmadı.",
          );
        }
      }
      setLoading(false);
    },
  });

  const handleLanguageButtonClick = (lang: string) => {
    setLangCode(lang);
    const existingTranslation = values.translations.find(
      (t) => t.lang_code === lang,
    );
    if (!existingTranslation) {
      setFieldValue("translations", [...values.translations]);
    }
  };

  const handleTranslationChange = (
    value: string,
    field: "name" | "description",
  ) => {
    const activeLang = langCode || defaultLangCode;
    if (!activeLang) return;

    const updatedTranslations = [...values.translations];
    const existingIndex = updatedTranslations.findIndex(
      (t) => t.lang_code === activeLang,
    );

    if (existingIndex !== -1) {
      updatedTranslations[existingIndex] = {
        ...updatedTranslations[existingIndex],
        [field]: value,
      };
    } else {
      updatedTranslations.push({
        lang_code: activeLang,
        name: field === "name" ? value : "",
        description: field === "description" ? value : "",
      });
    }

    setFieldValue("translations", updatedTranslations);

    // schema name/description tələb edirsə, default dildən sync et ki validation partlamasın
    if (activeLang === defaultLangCode) {
      if (field === "name") setFieldValue("name", value);
      if (field === "description") setFieldValue("description", value);
    }
  };

  const handleSwitchChange = async (
    checked: boolean,
    recordId: number,
    fieldName: string,
  ) => {
    try {
      await ProductsService.updateProduct({
        id: recordId,
        [fieldName]: checked,
      });

      if (fieldName === "is_visible") {
        checked
          ? message.success("Məhsul artıq saytda görünür.")
          : message.info("Məhsul artıq saytda görünmür.");
      } else if (fieldName === "most_sels") {
        checked
          ? message.success(
              "Məhsul artıq ən çox satılan məhsullar siyahısında görünür.",
            )
          : message.info(
              "Məhsul artıq ən çox satılan məhsullar siyahısında görünmür.",
            );
      } else if (fieldName === "is_new") {
        checked ? message.success("Yeni məhsul") : message.info("Köhnə məhsul");
      }

      setProducts((prevData) =>
        prevData.map((item) =>
          item.id === recordId ? { ...item, [fieldName]: checked } : item,
        ),
      );
    } catch (error) {
      message.error("Yeniləmə alınmadı.");
    }
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();

    setLangCode(defaultLangCode || "");
    setFieldValue("cover_image", "");
    setLogoFileList([]);
    setImageList([]);
    setSelectedCategoryId(null);
    setSelectedName(undefined);
    setSelectedBrandName(undefined);
    setSelectedColorName(undefined);
    setSelectedWarehouseName(undefined);
    setFieldValue("specs", []);
    setKey((prevKey) => prevKey + 1);
  };

  const handleEditClick = (record: ProductsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);

    const safeTranslations = (record.translations ?? []) as any[];
    const safeImages = (record.images ?? []) as any[];

    const fallbackLang =
      defaultLangCode || safeTranslations?.[0]?.lang_code || "";
    const fallbackTranslation = safeTranslations.find(
      (t) => t?.lang_code === fallbackLang,
    );

    resetForm({
      values: {
        id: String((record as any)?.id ?? ""),
        name: fallbackTranslation?.name ?? record.name ?? "",
        description:
          fallbackTranslation?.description ?? record.description ?? "",
        price: record.price ?? null,
        discount_price: record.discount_price ?? null,
        weight: record.weight ?? "",
        dimension: record.dimension ?? "",
        category_id: ((record as any).category_id ?? null) as any,
        is_visible: record.is_visible ?? true,
        country_id: ((record as any).country_id ?? null) as any,
        brand_id: ((record as any).brand_id ?? null) as any,
        most_sels: (record as any).most_sels ?? false,
        is_new: (record as any).is_new ?? true,
        translations: safeTranslations as any,
        color_id: (record as any).color_id ?? null,
        warehouse_id: (record as any).warehouse_id ?? null,
        specs: ((record as any).specs ?? []) as any,
        cover_image: record.cover_image ?? "",
        images: safeImages as any,
        product_group: "STATIC_GROUP_NAME",
        model_code: "STATIC_MODEL_CODE_001",
      },
    });

    setKey((prevKey) => prevKey + 1);
    setSelectedCategoryId(null);
    setLangCode(fallbackLang);

    setLogoFileList(
      record.cover_image
        ? [
            {
              uid: "-1",
              name: `${record.name ?? "product"} image`,
              status: "done",
              url: record.cover_image,
            },
          ]
        : [],
    );

    setImageList(
      (record.images ?? []).map((img: any, index: number) => ({
        uid: `${index}`,
        name: `${record.name ?? "product"} image ${index + 1}`,
        status: "done",
        url: img?.path ?? "",
      })),
    );
  };

  const handleDeleteClick = async (record: ProductsDataType) => {
    try {
      await ProductsService.deleteProduct(String(record?.id ?? ""));
      const updatedTotalCount = totalCount - 1;
      const newPage =
        currentPage > 1 && updatedTotalCount <= (currentPage - 1) * pageSize
          ? currentPage - 1
          : currentPage;

      setTotalCount(updatedTotalCount);
      setCurrentPage(newPage);
      await fetchProducts(newPage);

      message.success("Məhsul uğurla silindi!");
    } catch (error) {
      message.error("Məhsulu silmək alınmadı.");
    }
  };

  const columns: TableProps<ProductsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => ((a as any).key ?? 0) - ((b as any).key ?? 0),
    },
    {
      title: "Adı",
      dataIndex: "name",
      render: (name) => <span>{name || "Ad yoxdur"}</span>,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Təsviri",
      dataIndex: "description",
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>
          {(record.description || "").length > 15
            ? (record.description || "").substring(0, 15) + "..."
            : record.description || ""}
        </div>
      ),
      sorter: (a, b) =>
        (a.description || "").localeCompare(b.description || ""),
    },
    {
      title: "Qiymət",
      dataIndex: "price",
      sorter: (a, b) => (a.price ?? 0) - (b.price ?? 0),
    },
    // {
    //   title: "Endirim",
    //   dataIndex: "discount_price",
    //   render: (discount_price) => (
    //     <p>{discount_price ? discount_price : "-"}</p>
    //   ),
    //   sorter: (a, b) => (a.discount_price ?? 0) - (b.discount_price ?? 0),
    // },
    {
      title: "Kateqoriya",
      dataIndex: "category",
      render: (_, record) => (
        <p style={{ fontSize: "12px" }}>
          {record.category?.name || "Kateqoriya yoxdur"}
        </p>
      ),
    },
    // {
    //   title: "Seçimlər",
    //   dataIndex: "specs",
    //   render: (_, record) => (
    //     <div style={{ fontSize: "11px" }}>
    //       {(record.specs ?? []).length > 0
    //         ? (record.specs ?? []).map((el: any, index: number) => (
    //             <div key={index}>{el?.text ?? ""}</div>
    //           ))
    //         : "-"}
    //     </div>
    //   ),
    // },
    {
      title: "Rəng",
      dataIndex: "color_name",
      render: (color_name) => <span>{color_name || "-"}</span>,
    },
    // {
    //   title: "Model kod",
    //   dataIndex: "model_code",
    //   render: (model_code) => (
    //     <p style={{ fontSize: "12px" }}>{model_code ? model_code : "-"}</p>
    //   ),
    // },
    {
      title: "Şəkil",
      dataIndex: "cover_image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.cover_image ?? "");
            setShowImageModal(true);
          }}
        >
          {record.cover_image ? (
            <img
              style={{ width: "40px" }}
              src={record.cover_image}
              alt={`${(record.name || "img").slice(0, 5)}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: "Şəkillər",
      dataIndex: "images",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImages(
              (record.images ?? []).map((img: any) => img?.path ?? ""),
            );
            setImagesModal(true);
          }}
        >
          {(record.images ?? []).length
            ? (record.images ?? [])
                .slice(0, 2)
                .map((image: any, index: number) => (
                  <img
                    width={"30px"}
                    key={index}
                    src={image?.path ?? ""}
                    alt={`Şəkil ${index + 1}`}
                  />
                ))
            : null}
        </div>
      ),
    },
    // {
    //   title: "Çəki",
    //   dataIndex: "weight",
    //   render: (weight) => (
    //     <p style={{ fontSize: "12px" }}>{weight ? weight : "-"}</p>
    //   ),
    // },
    {
      title: "Brend",
      dataIndex: "brand_name",
      render: (brand_name) => (
        <p style={{ fontSize: "12px" }}>{brand_name ? brand_name : "-"}</p>
      ),
    },
    {
      title: "Ölkə",
      dataIndex: "country",
      render: (country) => (
        <p style={{ fontSize: "12px" }}>
          {country?.name ? country?.name : "-"}
        </p>
      ),
    },
    // {
    //   title: "Ölçü",
    //   dataIndex: "dimension",
    //   render: (dimension) => (
    //     <p style={{ fontSize: "12px" }}>{dimension ? dimension : "-"}</p>
    //   ),
    // },
    {
      title: "Görünürlük",
      dataIndex: "is_visible",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={!!record.is_visible}
            onChange={(checked) =>
              handleSwitchChange(checked, record.id, "is_visible")
            }
          />
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu səhifəni siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setSearchValue(v);
    setCurrentPage(1);

    if (v.trim() === "") {
      ProductsService.getAllProducts(currentPage, pageSize).then((response) => {
        const list: ProductsDataType[] = response?.data?.data ?? [];
        const dataWithKeys = list.map(
          (item: ProductsDataType, index: number) => ({
            ...item,
            key: (currentPage - 1) * pageSize + index + 1,
          }),
        );
        setProducts(dataWithKeys);
        setTotalCount(response?.data?.count ?? 0);
      });
    } else {
      ProductsService.getSearchData(v).then((response) => {
        const list: ProductsDataType[] = response?.data?.products?.data ?? [];
        const searchedData = list.map(
          (item: ProductsDataType, index: number) => ({
            ...item,
            key: index + 1,
          }),
        );
        setProducts(searchedData);
        setTotalCount(response?.data?.products?.total ?? searchedData.length);
      });
    }
  };

  const currentTranslation = useMemo(() => {
    const activeLang = langCode || defaultLangCode;
    return values.translations.find((t) => t.lang_code === activeLang);
  }, [values.translations, langCode, defaultLangCode]);

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Məhsullar</title>
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Məhsullar</h3>
          </div>

          <div className="pages_components">
            <div className="funcional_head">
              <MainButton onClick={handleAddClick}>
                <FaPlus size={20} />
              </MainButton>

              <div className="search">
                <Input
                  style={{ borderColor: "#03a9f4" }}
                  placeholder="Məhsulu axtarın..."
                  value={searchValue}
                  onChange={(e) => {
                    e.persist();
                    handleSearch(e);
                  }}
                />
              </div>
            </div>

            <div className="pages_table">
              <Table<ProductsDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={products}
                rowKey={(record) => String(record?.id ?? "")}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalCount,
                  showSizeChanger: true,
                  pageSizeOptions: ["5", "10", "20", "50", "100"],
                  showTotal: (total) => {
                    const displayedProducts = products.length;
                    const displayedText =
                      displayedProducts === pageSize
                        ? `Toplam ${total} məhsul var, Hal-hazırda ${pageSize} göstərilir`
                        : `Toplam ${total} məhsul var, Hal-hazırda ${displayedProducts} göstərilir`;

                    return (
                      <div style={{ position: "absolute", left: "10px" }}>
                        {displayedText}
                      </div>
                    );
                  },
                  responsive: true,
                  onChange: (page, pageSize) => {
                    setCurrentPage(page);
                    setPageSize(pageSize);
                  },
                  itemRender: (page, type, originalElement) => {
                    const totalPages = Math.ceil(totalCount / pageSize);

                    if (type === "page") {
                      if (page === 1 || page === 2 || page === totalPages) {
                        return originalElement;
                      }
                      if (page === currentPage) {
                        return originalElement;
                      }
                      if (page === 3 && currentPage > 4) {
                        return <span>...</span>;
                      }
                      if (
                        page === totalPages - 1 &&
                        currentPage < totalPages - 3
                      ) {
                        return <span>...</span>;
                      }
                      if (page >= currentPage - 2 && page <= currentPage + 2) {
                        return originalElement;
                      }
                      return null;
                    }
                    return originalElement;
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Məhsulu redaktə edin" : "Yeni məhsul əlavə edin"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedCategoryId(null);
          setSelectedName(undefined);
          setSelectedBrandName(undefined);
          setSelectedColorName(undefined);
          setSelectedWarehouseName(undefined);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
        width={"100%"}
      >
        <div>
          <div className="langs">
            {langs?.map((lang, index) => {
              return (
                <div key={index}>
                  <button
                    type="button"
                    className={lang.code === langCode ? "active_lang_btn" : ""}
                    onClick={() => handleLanguageButtonClick(lang.code)}
                  >
                    {lang.name}
                  </button>
                </div>
              );
            })}
          </div>

          <form className="modal_form" onSubmit={handleSubmit}>
            <div className="inputs">
              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="name">
                    Məhsulun adı {langCode}
                  </label>
                  <input
                    name="name"
                    id="name"
                    placeholder="Məhsulun adı"
                    value={currentTranslation?.name || ""}
                    onChange={(e) =>
                      handleTranslationChange(e.target.value, "name")
                    }
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage error={errors.name} touched={touched.name} />
              </div>

              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="description">
                    Məhsulun təsviri {langCode}
                  </label>
                  <input
                    name="description"
                    id="description"
                    placeholder="Məhsulun təsviri"
                    value={currentTranslation?.description || ""}
                    onChange={(e) =>
                      handleTranslationChange(e.target.value, "description")
                    }
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage
                  error={errors.description}
                  touched={touched.description}
                />
              </div>

              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="price">
                    Qiymət
                  </label>
                  <input
                    type="number"
                    name="price"
                    id="price"
                    placeholder="Qiymət"
                    value={values.price ?? ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
                <ErrorMessage error={errors.price} touched={touched.price} />
              </div>

              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="discount_price">
                    Endrimli qiymət
                  </label>
                  <input
                    type="number"
                    name="discount_price"
                    id="discount_price"
                    placeholder="Endrimli qiymət"
                    value={values.discount_price ?? ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
              </div>

              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="weight">
                    Məhsulun çəkisi
                  </label>
                  <input
                    type="text"
                    name="weight"
                    id="weight"
                    placeholder="Məhsulun çəkisi"
                    value={values.weight || ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
              </div>

              <div className="form_input products_form_input">
                <div className="input_wrapper">
                  <label className="label" htmlFor="dimension">
                    Məhsulun ölçüsü
                  </label>
                  <input
                    type="text"
                    name="dimension"
                    id="dimension"
                    placeholder="Məhsulun ölçüsü"
                    value={values.dimension || ""}
                    onChange={handleChange}
                  />
                  <span className="focus_border"></span>
                </div>
              </div>

              <div className="form_input products_form_input">
                <SelectCountry
                  setFieldValue={setFieldValue}
                  country_id={editingRecord?.country_id ?? null}
                  selectedName={selectedName}
                  setSelectedName={setSelectedName}
                />
              </div>

              <div className="form_input products_form_input">
                <SelectBrand
                  setFieldValue={setFieldValue}
                  brand_id={editingRecord?.brand_id ?? null}
                  selectedName={selectedBrandName}
                  setSelectedName={setSelectedBrandName}
                />
              </div>

              <div className="form_input products_form_input">
                <SelectColor
                  setFieldValue={setFieldValue}
                  color_id={editingRecord?.color_id ?? null}
                  selectedName={selectedColorName}
                  setSelectedName={setSelectedColorName}
                  field={"color_id"}
                />
              </div>

              <div className="form_input products_form_input">
                <SelectCategory
                  setFieldValue={setFieldValue}
                  categroryName={editingRecord?.category?.name}
                  selectedCategoryId={selectedCategoryId}
                  setSelectedCategoryId={setSelectedCategoryId}
                />
                <ErrorMessage
                  error={errors.category_id}
                  touched={touched.category_id}
                />
              </div>
            </div>

            <div className="form_input_">
              <SelectOptions
                visible={key}
                setFieldValue={setFieldValue}
                specs={editingRecord?.specs ? editingRecord?.specs : []}
              />
            </div>

            <div className="form_input">
              <SelectImage
                image_name="Banner şəkli"
                logoFileList={logoFileList}
                setLogoFileList={setLogoFileList}
                field="cover_image"
                setFieldValue={setFieldValue}
              />
              <ErrorMessage
                error={errors.cover_image}
                touched={touched.cover_image}
              />
            </div>

            <DragImages
              imageList={imageList}
              setImageList={setImageList}
              setFieldValue={setFieldValue}
            />

            <div className="form_input">
              <label className="active_label" htmlFor="">
                Məhsulun saytda görünməsi
              </label>
              <Switch
                checked={!!values.is_visible}
                onChange={(checked) => setFieldValue("is_visible", checked)}
              />
            </div>

            <div className="form_input">
              <label className="active_label" htmlFor="">
                Yeni məhsul
              </label>
              <Switch
                checked={!!values.is_new}
                onChange={(checked) => setFieldValue("is_new", checked)}
              />
            </div>

            <MainButton full type="submit">
              Əlavə edin
            </MainButton>
          </form>
        </div>
      </Modal>

      <ProductImages
        imagesModal={imagesModal}
        setImagesModal={setImagesModal}
        images={selectedImages}
      />
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Products;
